import type {
  Business,
  NamedEntity,
  Nil,
  Patient,
  User,
} from '@pbt/pbt-ui-components'

import type {
  BusinessCatalogItemVariation,
  Prescription as SchemaPrescription,
  RxPrescriptionOrigin,
  RxType,
} from '~/api/graphql/generated/types'
import type InvoiceType from '~/constants/InvoiceType'
import type {
  AdministrationCourseType,
  PrescriptionType,
} from '~/constants/prescription'
import type { OrderType } from '~/constants/SOAPStates'

import type { Invoice } from './finance'
import type { InventoryItem, Variation } from './inventories'
import type {
  ChargeData,
  DispensedFromData,
  Order,
  OrderProcedure,
} from './orders'
import type { ClientTask, Task } from './tasks'

export type PrescriptionV2 = SchemaPrescription

export type InventoryLineItem = {
  businessReferenceNumber?: string
  inventoryShipmentItemId: string
  lineItemQuantity?: string
  lotNumber?: string
  manufacturerName?: string
  serialNumber?: string
}

export type Prescription = NamedEntity &
  DispensedFromData &
  ChargeData &
  PrescriptionQuantityData &
  PrescriptionAdministrationInfo & {
    administrationCourseLength: number
    administrationCourseType: AdministrationCourseType
    autoshipFrequency?: number | Nil
    autoshipUnitId?: string | Nil
    bundleId?: string | Nil
    businessId: string
    cancelledBy?: User
    catalogVariation?: BusinessCatalogItemVariation
    categoryId?: string | Nil
    client?: User
    clientId: string | Nil
    clientTasks?: ClientTask | Nil
    compoundingReason?: string | null
    compoundingReasonId?: string | null
    controlled?: boolean
    createdAt?: string
    creationDate?: string
    deleted?: boolean | Nil
    dispenseReason: string
    doctor?: PrescriptionShort['doctor']
    doctorId: string | null
    drugInfo?: PrescriptionDrugInfo
    drugMasterName?: string | Nil
    eventId: string | null
    filledBy?: User
    filledById: string
    globalFoodCatalogId?: string | Nil
    globalFoodCatalogVariationId?: string | Nil
    globalInventory?: InventoryItem
    globalInventoryId: string
    globalInventoryMapping?: InventoryItem
    globalInventoryMappingId?: string
    globalVariation?: Variation
    globalVariationId: string
    globalVariationMapping?: Variation
    globalVariationMappingId?: string
    group?: string | Nil
    inventory?: InventoryItem
    inventoryId: string
    inventoryShipmentItemLogs?: InventoryLineItem[]
    invoice?: Invoice
    invoiceId?: string | null
    isInvoicePosted?: boolean | Nil
    isOTC?: boolean
    lineItem?: Order
    logId: string | null
    masterName?: string
    mismatchWarning?: string | Nil
    modificationDate?: string
    notes?: string
    numberRefills?: number
    orderNumber?: string | null
    origin?: RxPrescriptionOrigin | Nil
    parent?: Prescription
    parentId: string | null
    partNumber?: string | Nil
    patient?: Patient
    patientId: string | Nil
    pdfUrl?: string | Nil
    prescribedDate?: string
    prescriptionType: PrescriptionType
    quantityNumbRefills: number
    quantityPerRefill: number
    quantityPerRefillCustomUnit?: string | Nil
    quantityPerRefillUnitId?: string | Nil
    quantityRefillExpirationDate: string
    quantityUnitId?: string | Nil
    refillCancelDate?: string
    refillExpirationDate?: string
    refillNotes?: string
    refillOrdinalNumber?: string
    refillRemains: number
    refills?: Prescription[] | Nil
    rejectionReason?: string | Nil
    retailOrderLineItem?: {
      // BE does not pass us the data in the correct RetailOrderLineItem format.
      autoshipFrequency?: number | Nil
      autoshipUnitId?: string | Nil
      invoiceType?: InvoiceType | Nil
      isSoapFinalized?: boolean | Nil
    }
    rxNo?: string
    rxType?: RxType | Nil
    signatureUrl?: string
    signedDate?: string
    signer?: PrescriptionShort['doctor']
    signerId?: string
    soapId: string | null
    stateId?: string
    storeDefault?: boolean | Nil
    task?: Task
    totalQuantity?: number
    type: OrderType
    updatedAt?: string
    variationId?: string
  }

export type PrescriptionAdministrationInfo = {
  administrationCourseLength?: number
  administrationCourseType?: AdministrationCourseType
  administrationFrequencyId?: string
  administrationFrequencyNotes?: string
  administrationInstructionsAutofill?: boolean
  administrationQuantity?: number
  administrationQuantityCustomUnit?: string
  administrationQuantityUnitId?: string
  administrationRouteId?: string | Nil
  notes?: string
  totalQuantity?: number
  variation?: Variation
}

export type PrescriptionQuantityData = {
  discussedWithClient?: boolean
  quantityDrugExpirationDate?: string
  totalQuantityCustomUnit?: string
  totalQuantityUnitId?: string
}

export type PrescriptionPrintInfo = Prescription & {
  bottomWarning?: string
  business?: Business
  warning?: string
}

export type PrescriptionShort = NamedEntity & {
  doctor?: User
  imagingOrderId?: string
  inventoryLogStatusId?: string
  notes?: string | Nil
  origin?: RxPrescriptionOrigin | Nil
  parent?: PrescriptionShort
  parentId?: string
  prescriptionType?: PrescriptionType
  procedure?: OrderProcedure | Nil
  quantityNumbRefills?: number
  refillOrdinalNumber?: number
}

export type PrescriptionDrugInfo = {
  compounded?: boolean | Nil
  count?: number | Nil
  customCompound?: boolean | Nil
  customForm: string | Nil
  deliveryMethodId: string | Nil
  description: string | Nil
  flavor?: string | Nil
  formId: string | Nil
  packageTypeId?: string | Nil
  perPackageAmount?: number | Nil
  perPackageUnitsId?: string | Nil
  saleUnitOfMeasure?: string | Nil
  strength: number | Nil
  strengthColorId: string | Nil
  strengthUnitsId: string | Nil
  variationName?: string | Nil
}

export type SignatureData = {
  signatureUrl: string | undefined
  signedDate: string | undefined
  signer: User | undefined
  signerId: string | undefined
}

export type GetPrescriptionType = {
  origin?: RxPrescriptionOrigin | Nil
  prescriptionType: PrescriptionType | undefined
}

export const DOSAGE_FORM_ID = '7NOogO4G'
export const DOSAGE_FORM_V2_ID = 'gXOldODm'
